import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useStore } from 'zustand';

import { type Bounds } from '@/domain/geometry';
import { type Vec2 } from '@/domain/math';
import { DEFAULT_STYLE, type EntityId, showsPanels, visibilityFor } from '@/domain/model';
import { type ControllerView, type Modifiers, type SnapResult, type ToolController } from '@/input';
import {
  type Camera,
  DEFAULT_CAMERA,
  framedBounds,
  labelSize,
  type Renderer,
  resetZoom,
  selectedValues,
  type SnapMarker,
  zoomStep,
} from '@/rendering';
import { type EditorStore } from '@/store';

import { type AccountApi } from './accountPort';
import { AnglePanel } from './AnglePanel';
import { CanvasHost, type PointerGesture } from './canvas/CanvasHost';
import { ContextMenu, type MenuItem } from './ContextMenu';
import { FileBar, type PictureFormat, type PrintPaper, type SaveState } from './FileBar';
import { GraphPanel } from './GraphPanel';
import { Inspector } from './Inspector';
import { LayersPanel } from './LayersPanel';
import { LessonBar } from './LessonBar';
import { MathText } from './MathText';
import { MeasurementsPanel } from './MeasurementsPanel';
import { NamePanel } from './NamePanel';
import { PlanePanel } from './PlanePanel';
import { PropertiesPanel } from './PropertiesPanel';
import { RatioPanel } from './RatioPanel';
import { type ReportApi } from './reportPort';
import { type FigureOutcome, ScriptPanel } from './ScriptPanel';
import { SolidPanel } from './SolidPanel';
import { TextEditor } from './TextEditor';
import { colour, radius, space, text } from './theme';
import { Toolbar, type ToolbarEntry } from './Toolbar';
import { ToolPrompt } from './ToolPrompt';
import { TransformPanel } from './TransformPanel';
import { useDismissOnOutside } from './useDismissOnOutside';

/**
 * Application shell.
 *
 * Owns no editor logic. It receives a `ToolController` and an `EditorStore` from the
 * composition root, wires pointer gestures to the controller, and pushes the resulting
 * view into the renderer.
 *
 * There are two paths out of the controller, deliberately:
 *
 * - **To the renderer**, directly, bypassing React. Scene, preview and snap change on
 *   every pointer move; a reconciliation pass per mouse event would not hold 60 FPS.
 * - **To the store**, which the panels select from. Only what a panel displays goes
 *   here, so restyling an object does not re-render the object list.
 *
 * The shell keeps just the state React genuinely owns: the camera readout, the cursor and
 * import warnings.
 */

/** File actions, supplied by the composition root. */
export interface FileActions {
  readonly subscribeSaveState: (
    listener: (state: { status: string; lastSavedAt: number | null; error: string | null }) => void
  ) => () => void;
  readonly currentSaveState: () => {
    status: string;
    lastSavedAt: number | null;
    error: string | null;
  };
  readonly warnings: readonly string[];
  exportFile(): void;
  /** `aspect` is the width ÷ height to pad the file to; omitted keeps the drawing's shape. */
  exportDrawing(
    format: PictureFormat,
    only?: ReadonlySet<EntityId>,
    aspect?: number
  ): Promise<void>;
  printDrawing(paper: PrintPaper): Promise<void>;
  importFile(file: File): Promise<readonly string[]>;
  /** Places a picture at the centre of the view. Resolves to a warning when it cannot. */
  importImage(file: File, at: Vec2): Promise<string | null>;
  saveNow(): Promise<void>;
  /** The drawing as JSON text. Read by the crash download and by a bug report. */
  snapshot(): string;
}

export interface AppProps {
  readonly controller: ToolController;
  readonly store: EditorStore;
  readonly tools: readonly ToolbarEntry[];
  readonly file: FileActions;
  /** Turns a problem statement into a script. Absent when no model proxy is configured. */
  readonly drawFromProblem?: (problem: string) => Promise<FigureOutcome>;
  /** The account port, from the composition root. Absent when the deployment has no accounts. */
  readonly accountApi?: AccountApi | undefined;
  /** The report port, from the composition root. Absent when there is nowhere to send one. */
  readonly reportApi?: ReportApi | undefined;
  readonly onRendererReady?: (renderer: Renderer) => void;
  readonly initialFit?: Bounds;
}

export function App({
  controller,
  store,
  tools,
  file,
  drawFromProblem,
  accountApi,
  reportApi,
  onRendererReady,
  initialFit,
}: AppProps) {
  const [camera, setCamera] = useState<Camera>(DEFAULT_CAMERA);
  const [cursor, setCursor] = useState('default');
  const [scriptOpen, setScriptOpen] = useState(false);
  // The canonical React way to read an external store: no setState-in-effect, and no
  // window between the first render and the subscription where an update could be lost.
  const save = useSyncExternalStore(file.subscribeSaveState, file.currentSaveState);
  const [warnings, setWarnings] = useState<readonly string[]>(file.warnings);
  const rendererRef = useRef<Renderer | null>(null);

  const activeToolId = useStore(store, (state) => state.activeToolId);
  /**
   * The label currently being edited.
   *
   * Holds the anchor in **world** coordinates, not screen. A screen position captured when the
   * editor opened is correct for exactly one camera: zoom the wheel while typing and the input
   * stays where it was while the drawing slides out from under it. Keeping the world point and
   * converting on every render costs one matrix multiply and keeps the input on its label.
   */
  const [textEdit, setTextEdit] = useState<{
    id: EntityId;
    world: Vec2;
    screen: { x: number; y: number };
    initial: string;
    fontPx: number;
    colour: string;
  } | null>(null);
  const lesson = useStore(store, (state) => state.lesson);
  const showGridAxes = useStore(store, (state) => state.showGridAxes);
  const prompt = useStore(store, (state) => state.prompt);
  const document = useStore(store, (state) => state.document);
  const style = useStore(store, (state) => state.style);
  const appliesToSelection = useStore(store, (state) => state.styleAppliesToSelection);
  const selection = useStore(store, (state) => state.selection);
  const selectionCount = selection.size;
  const canUndo = useStore(store, (state) => state.canUndo);
  const canRedo = useStore(store, (state) => state.canRedo);
  const undoLabel = useStore(store, (state) => state.undoLabel);
  const redoLabel = useStore(store, (state) => state.redoLabel);
  const commands = useStore(store, (state) => state.commands);

  // Presentation mode clears the screen of everything but the drawing and the replay.
  const panels = showsPanels(lesson.mode);

  /* Push the controller's view into the renderer whenever it changes. */
  useEffect(() => {
    const push = (view: ControllerView): void => {
      const renderer = rendererRef.current;
      if (renderer === null) return;

      renderer.setScene(view.editor.scene);
      renderer.setOverlay({
        // The highlight rides with the selection so it gets the same halo: a panel naming a
        // point has to be able to answer "which one is that?" without selecting it, because
        // selecting it would close the very panel that asked.
        selected: selectedValues([...view.editor.selection, ...view.highlight], (id) => {
          const entity = view.editor.document.entities.get(id);
          if (entity === undefined) return null;
          return { value: view.editor.scene.values.get(id) ?? null, style: entity.style };
        }),
        preview: view.preview,
        snap: markerFor(view.snap),
        box: view.transformBox,
      });
      // The cursor is the one view field React owns, because it lands on a DOM attribute.
      // Everything else a panel needs arrives through the store.
      setCursor(view.cursor);

      /*
       * A label wanting an editor. The position is converted here rather than in the
       * controller, because only the renderer knows the camera and the viewport — and the
       * editor is a DOM element that has to land on the right pixel.
       */
      const request = view.textEdit;
      if (request !== null) {
        const entity = view.editor.document.entities.get(request.id);
        const style = entity?.style ?? DEFAULT_STYLE;
        setTextEdit({
          id: request.id,
          world: request.at,
          screen: renderer.toScreen(request.at),
          initial: entity?.source.op === 'text' ? entity.source.content : '',
          fontPx: labelSize(style),
          colour: style.stroke,
        });
        controller.clearTextEdit();
      }
    };

    push(controller.view);
    return controller.subscribe(push);
  }, [controller]);

  const handleExport = useCallback(() => {
    file.exportFile();
  }, [file]);

  /**
   * Places a picture at the middle of what the user is looking at.
   *
   * The centre of the *viewport*, not of the drawing: an image that arrived at the origin
   * while the user was working somewhere else would look like nothing happened at all.
   */
  const handleImportImage = useCallback(
    (picked: File) => {
      const renderer = rendererRef.current;
      const view = renderer?.visibleWorldBounds;
      const at =
        view === undefined
          ? { x: 0, y: 0 }
          : { x: (view.minX + view.maxX) / 2, y: (view.minY + view.maxY) / 2 };

      void file
        .importImage(picked, at)
        .then((problem) => {
          setWarnings(problem === null ? [] : [problem]);
        })
        .catch((error: unknown) => {
          setWarnings([error instanceof Error ? error.message : String(error)]);
        });
    },
    [file]
  );

  const handleImport = useCallback(
    (picked: File) => {
      void file
        .importFile(picked)
        .then(setWarnings)
        .catch((error: unknown) => {
          // A file that cannot be read is reported, not swallowed: the user needs to
          // know the import did nothing rather than wonder why the canvas is unchanged.
          setWarnings([error instanceof Error ? error.message : String(error)]);
        });
    },
    [file]
  );

  /*
   * The lesson view reaches the canvas as a *filter*, never as an edit. A paused replay
   * hides objects from the picture while leaving the document, the history and the object
   * list whole — which is why a worksheet saved mid-replay arrives complete.
   */
  useEffect(() => {
    rendererRef.current?.setVisibilityFilter(visibilityFor(document, lesson));
  }, [document, lesson]);

  /*
   * The axes are backdrop rather than drawing, so they take the other route: straight to the
   * grid layer, without passing through the visibility filter, which answers about entities.
   * The renderer ignores a value it already holds, so the first pass after mount is free.
   */
  useEffect(() => {
    rendererRef.current?.setGridAxesVisible(showGridAxes);
  }, [showGridAxes]);

  /*
   * The editor follows its label when the camera moves.
   *
   * The screen position is derived from the world anchor rather than captured once, because a
   * position that is correct for one camera is wrong for every other: zoom the wheel while
   * typing and the input stays put while the drawing slides out from under it. Recomputed in
   * an effect rather than during render, so the renderer ref is only read after commit.
   */
  useEffect(() => {
    const renderer = rendererRef.current;
    if (renderer === null || textEdit === null) return;

    const screen = renderer.toScreen(textEdit.world);
    if (screen.x === textEdit.screen.x && screen.y === textEdit.screen.y) return;

    setTextEdit({ ...textEdit, screen });
  }, [camera, textEdit]);

  const handleExportPicture = useCallback(
    (format: PictureFormat, onlySelection: boolean, aspect: number | undefined) => {
      // A fresh set: the store's selection is live, and an export that reads it later would
      // export whatever happened to be selected by the time the encoder finished.
      const only = onlySelection ? new Set(selection) : undefined;

      void file.exportDrawing(format, only, aspect).catch((error: unknown) => {
        // An export that fails silently leaves the user waiting for a download that will
        // never arrive, so the reason surfaces in the same place import errors do.
        setWarnings([error instanceof Error ? error.message : String(error)]);
      });
    },
    [file, selection]
  );

  const handlePrint = useCallback(
    (paper: PrintPaper) => {
      void file.printDrawing(paper).catch((error: unknown) => {
        setWarnings([error instanceof Error ? error.message : String(error)]);
      });
    },
    [file]
  );

  const handleSaveNow = useCallback(() => {
    void file.saveNow();
  }, [file]);

  const handleReady = useCallback(
    (renderer: Renderer) => {
      rendererRef.current = renderer;
      controller.setZoom(renderer.camera.zoom);
      renderer.setScene(controller.view.editor.scene);
      onRendererReady?.(renderer);
    },
    [controller, onRendererReady]
  );

  const handleCameraChange = useCallback(
    (next: Camera) => {
      setCamera(next);
      // Pick tolerances are expressed in screen pixels, so the controller has to know.
      controller.setZoom(next.zoom);
    },
    [controller]
  );

  /**
   * The view shortcuts: frame the drawing, frame the selection, step the zoom, back to 1:1.
   *
   * Held here rather than in the tool controller because the camera is not part of the
   * document. A tool decides what an edit means; where the user happens to be looking is a
   * fact about this window, and nothing about it belongs in undo or in a saved file.
   *
   * Matched on `event.code`, not `event.key`. `code` names the physical key, so ⇧1 is ⇧1
   * whatever the layout produces — which matters here because a Vietnamese input method is
   * usually sitting between the keyboard and the page, and `key` is what it rewrites.
   *
   * Returns whether the key was used, so an unclaimed one still reaches the tools.
   */
  const viewKeyDown = useCallback(
    (event: KeyboardEvent): boolean => {
      const renderer = rendererRef.current;
      if (renderer === null) return false;

      const middle = { x: renderer.viewport.width / 2, y: renderer.viewport.height / 2 };
      const frame = (selection: ReadonlySet<EntityId>): boolean => {
        const target = framedBounds(renderer.scene, selection);
        // Nothing with any extent — an empty document, or one holding only lines. Leaving the
        // camera alone is the honest answer; flinging it at a rectangle of no size is not.
        if (target === null) return true;
        renderer.fit(target);
        handleCameraChange(renderer.camera);
        return true;
      };

      if (event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey) {
        if (event.code === 'Digit1') return frame(new Set());
        if (event.code === 'Digit2') return frame(controller.view.editor.selection);
      }

      if (!event.ctrlKey && !event.metaKey) return false;

      const stepped =
        event.code === 'Digit0'
          ? resetZoom(renderer.camera, renderer.viewport, middle)
          : event.code === 'Equal' || event.code === 'NumpadAdd'
            ? zoomStep(renderer.camera, renderer.viewport, middle, 1)
            : event.code === 'Minus' || event.code === 'NumpadSubtract'
              ? zoomStep(renderer.camera, renderer.viewport, middle, -1)
              : null;

      if (stepped === null) return false;
      renderer.setCamera(stepped);
      handleCameraChange(stepped);
      return true;
    },
    [controller, handleCameraChange]
  );

  /*
   * Stable, and that is the whole point of the `useCallback`.
   *
   * A panel keeps a point lit through an effect, and an effect depends on the function it calls.
   * An inline arrow is a new function on every render, so the effect re-ran on every render, lit
   * the point again, and that emission re-rendered the shell — a loop that React eventually
   * stopped with "Maximum update depth exceeded".
   */
  const highlight = useCallback(
    (ids: readonly EntityId[]) => {
      controller.setHighlight(ids);
    },
    [controller]
  );

  const toModifiers = (gesture: PointerGesture): Modifiers => gesture.modifiers;

  const handlePointerDown = useCallback(
    (gesture: PointerGesture) => {
      controller.pointerDown(gesture.world, gesture.screen, gesture.button, toModifiers(gesture));
    },
    [controller]
  );

  const handlePointerMove = useCallback(
    (gesture: PointerGesture) => {
      controller.pointerMove(gesture.world, gesture.screen, toModifiers(gesture));
    },
    [controller]
  );

  const handlePointerUp = useCallback(
    (gesture: PointerGesture) => {
      controller.pointerUp(gesture.world, gesture.screen, gesture.button, toModifiers(gesture));
    },
    [controller]
  );

  /**
   * Double-clicking a label opens its editor, whichever tool is in hand.
   *
   * The brief asks for it, and it is the gesture a user tries first — a label that can only
   * be edited by first switching tools is one they will retype instead.
   */
  const handleDoubleClick = useCallback(
    (gesture: PointerGesture) => {
      const hit = hitAt(controller, gesture.world);
      if (hit !== null) controller.requestTextEdit(hit);
    },
    [controller]
  );

  const handlePointerLeave = useCallback(() => {
    controller.cancel();
  }, [controller]);

  const [menu, setMenu] = useState<{ at: Vec2; items: readonly MenuItem[] } | null>(null);
  const closeMenu = useCallback(() => {
    setMenu(null);
  }, []);

  /**
   * What a right-click offers, decided from what it landed on.
   *
   * Right-clicking something that is *not* selected selects it first. That is the behaviour of
   * every editor with a context menu, and the reason is that the menu's items act on the
   * selection: without it, "Xoá" would delete whatever happened to be selected elsewhere while
   * the user was pointing at something else entirely.
   *
   * Right-clicking *inside* an existing selection leaves it alone, so a menu opened over four
   * chosen objects still acts on all four.
   */
  const handleContextMenu = useCallback(
    (gesture: PointerGesture) => {
      const target = controller.entityAt(gesture.world);
      const selection = controller.view.editor.selection;

      if (target !== null && !selection.has(target)) controller.select([target]);
      const acting = target !== null || selection.size > 0;

      const entity =
        target === null ? null : (controller.view.editor.document.entities.get(target) ?? null);

      // The discoverable half of alt-click. The fast gesture is invisible; a menu item is how
      // anyone finds out the edge can be taken out at all.
      const liftable =
        controller.edgeUnder(gesture.world) !== null ||
        controller.sideUnder(gesture.world) !== null;

      const items: MenuItem[] = acting
        ? [
            ...(liftable
              ? [
                  {
                    label: 'Tách cạnh này ra',
                    shortcut: 'Alt+click',
                    onSelect: () => {
                      controller.promoteEdgeAt(gesture.world);
                    },
                  },
                ]
              : []),
            {
              label: 'Nhân bản',
              shortcut: 'Ctrl+D',
              separated: liftable,
              onSelect: commands.duplicate,
            },
            { label: 'Sao chép', shortcut: 'Ctrl+C', onSelect: commands.copy },
            { label: 'Cắt', shortcut: 'Ctrl+X', onSelect: commands.cut },
            {
              label: 'Dán',
              shortcut: 'Ctrl+V',
              onSelect: commands.paste,
              disabled: !controller.canPaste,
            },
            ...(entity === null
              ? []
              : [
                  {
                    label: 'Đưa lên trên',
                    separated: true,
                    onSelect: () => {
                      commands.moveInDrawOrder(entity.id, 1);
                    },
                  },
                  {
                    label: 'Đưa xuống dưới',
                    onSelect: () => {
                      commands.moveInDrawOrder(entity.id, -1);
                    },
                  },
                  {
                    label: entity.visible ? 'Ẩn' : 'Hiện',
                    separated: true,
                    onSelect: () => {
                      commands.setPresentation([...controller.view.editor.selection], {
                        visible: !entity.visible,
                      });
                    },
                  },
                  {
                    label: entity.locked ? 'Mở khoá' : 'Khoá',
                    onSelect: () => {
                      commands.setPresentation([...controller.view.editor.selection], {
                        locked: !entity.locked,
                      });
                    },
                  },
                ]),
            ...(controller.canDetach
              ? [
                  {
                    label: 'Tách rời khỏi hình khác',
                    separated: true,
                    onSelect: () => {
                      commands.detach();
                    },
                  },
                ]
              : []),
            {
              label: 'Xoá',
              shortcut: 'Del',
              separated: true,
              destructive: true,
              onSelect: () => {
                commands.remove([...controller.view.editor.selection]);
              },
            },
          ]
        : [
            {
              label: 'Dán',
              shortcut: 'Ctrl+V',
              onSelect: commands.paste,
              disabled: !controller.canPaste,
            },
            {
              label: 'Chọn tất cả',
              shortcut: 'Ctrl+A',
              onSelect: () => {
                controller.keyDown('a', { shift: false, alt: false, primary: true });
              },
            },
            {
              label: 'Vừa khung hình',
              shortcut: '⇧1',
              separated: true,
              onSelect: () => {
                const renderer = rendererRef.current;
                if (renderer === null) return;
                const box = framedBounds(renderer.scene);
                if (box !== null) {
                  renderer.fit(box);
                  handleCameraChange(renderer.camera);
                }
              },
            },
          ];

      /*
       * `gesture.screen` is measured inside the canvas, and the menu is positioned `fixed`
       * against the viewport. The two agree because the shell fills the viewport and the canvas
       * fills the shell — both `inset: 0` — which is what makes this a shell rather than a
       * widget, and is the assumption to revisit if it ever stops being one.
       */
      setMenu({ at: gesture.screen, items });
    },
    [commands, controller, handleCameraChange]
  );

  /* Keyboard: tool shortcuts, Escape, Delete. */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target;
      // Never steal keys from a text field.
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return;
      if (event.code === 'Space') return; // reserved for pan

      if (viewKeyDown(event)) {
        event.preventDefault();
        return;
      }

      const consumed = controller.keyDown(event.key, {
        shift: event.shiftKey,
        alt: event.altKey,
        primary: event.ctrlKey || event.metaKey,
      });
      if (consumed) event.preventDefault();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [controller, viewKeyDown]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        overflow: 'hidden',
        userSelect: 'none',
        touchAction: 'none',
        fontFamily: 'var(--es-font)',
        color: colour.strong,
      }}
    >
      <CanvasHost
        onCameraChange={handleCameraChange}
        onReady={handleReady}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerLeave}
        onDoubleClick={handleDoubleClick}
        onContextMenu={handleContextMenu}
        cursor={cursor}
        {...(initialFit === undefined ? {} : { initialFit })}
      />
      {menu !== null && <ContextMenu at={menu.at} items={menu.items} onClose={closeMenu} />}
      {/*
        One centred column at the top, rather than two components each placing themselves.
        They previously claimed the same absolute slot and the lesson bar covered the file
        controls. Stacking them here also means the lesson bar rises to the top of its own
        accord in presentation mode, when the file bar is not rendered at all.
      */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: 12,
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          // The column is as wide as its widest bar; the gap between them must not swallow
          // clicks meant for the drawing underneath.
          pointerEvents: 'none',
        }}
      >
        {panels && (
          <FileBar
            saveState={save.status as SaveState}
            lastSavedAt={save.lastSavedAt}
            error={save.error}
            warnings={warnings}
            onExport={handleExport}
            onExportPicture={handleExportPicture}
            selectionCount={selectionCount}
            onPrint={handlePrint}
            onOpenScript={() => {
              setScriptOpen(true);
            }}
            onImport={handleImport}
            onImportImage={handleImportImage}
            onClear={commands.clearDocument}
            onSaveNow={handleSaveNow}
            {...(accountApi === undefined ? {} : { accountApi })}
            {...(reportApi === undefined ? {} : { reportApi })}
            snapshot={() => file.snapshot()}
          />
        )}
        <LessonBar store={store} />
      </div>

      {panels && (
        <Toolbar
          tools={tools}
          activeId={activeToolId}
          onSelect={commands.setActiveTool}
          history={{
            canUndo,
            canRedo,
            undoLabel,
            redoLabel,
            onUndo: commands.undo,
            onRedo: commands.redo,
          }}
        />
      )}
      {/*
        One rail, in the order a user asks their questions.

        `Objects` is the navigator — what is in the drawing. Everything under it is the
        inspector, and it is contextual: it shows what the current selection affords and
        nothing else. `Kiểu vẽ` sits last because it is the only section that never
        disappears; anywhere else it would shove the contextual sections up and down the rail
        every time the selection changed.
      */}
      {panels && (
        <Inspector>
          <LayersPanel store={store} />
          <NamePanel store={store} />
          <TransformPanel store={store} />
          <SolidPanel store={store} />
          <RatioPanel store={store} />
          <PlanePanel store={store} />
          <AnglePanel store={store} onHighlight={highlight} />
          <GraphPanel store={store} />
          <MeasurementsPanel store={store} />
          <PropertiesPanel
            style={style}
            appliesToSelection={appliesToSelection}
            selectionCount={selectionCount}
            onChange={commands.setStyle}
          />
        </Inspector>
      )}

      {textEdit !== null && (
        <TextEditor
          key={textEdit.id}
          at={textEdit.screen}
          initial={textEdit.initial}
          fontPx={textEdit.fontPx}
          colour={textEdit.colour}
          onCommit={(content) => {
            controller.setText(textEdit.id, content);
            setTextEdit(null);
          }}
          onCancel={() => {
            // An abandoned empty label is removed by `setText`, so cancelling a brand-new
            // one leaves nothing behind.
            controller.setText(textEdit.id, textEdit.initial);
            setTextEdit(null);
          }}
        />
      )}

      {scriptOpen && panels && (
        <ScriptPanel
          store={store}
          {...(drawFromProblem === undefined ? {} : { onDrawFromProblem: drawFromProblem })}
          onClose={() => {
            setScriptOpen(false);
          }}
        />
      )}

      {prompt !== null && (
        <ToolPrompt
          tool={prompt.tool}
          step={prompt.step}
          steps={prompt.steps}
          hint={prompt.hint}
          {...(prompt.problem === undefined ? {} : { problem: prompt.problem })}
        />
      )}

      <StatusBar camera={camera} />
      <Hints />
    </div>
  );
}

/**
 * Camera readout.
 *
 * Bottom-centre rather than bottom-left, where it used to sit: the measurements table
 * claimed that corner, and a readout that overlaps a table the user is reading is worse
 * than one they have to glance further for.
 */
/**
 * The label under a point, if any.
 *
 * Uses the controller's own view rather than a second hit test with different tolerances:
 * two answers to "what is under the cursor" is one too many.
 */
/**
 * Which marker the overlay draws for a snap.
 *
 * A real mapping rather than a cast. The two vocabularies overlap by name — `point`, `curve`,
 * `grid`, `intersection` — and casting worked for exactly those, so the two kinds it did *not*
 * cover fell through the painter's switch and drew nothing at all: a snap to a solid's edge, and
 * now a snap to a solid's corner, gave the user no indication that anything had been caught.
 *
 * A corner takes the ring, which is the marker that means "this click reuses that point" — and
 * that is precisely what clicking a corner does. An edge takes the diamond, which means the point
 * will be locked to what it is riding on.
 */
function markerFor(
  snap: SnapResult | null
): { readonly world: Vec2; readonly kind: SnapMarker } | null {
  if (snap === null) return null;

  const kind = SNAP_MARKERS[snap.kind];
  return kind === undefined ? null : { world: snap.world, kind };
}

const SNAP_MARKERS: Readonly<Record<string, SnapMarker>> = {
  grid: 'grid',
  point: 'point',
  curve: 'curve',
  intersection: 'intersection',
  'solid-vertex': 'point',
  'solid-edge': 'curve',
};

function hitAt(controller: ToolController, world: Vec2): EntityId | null {
  for (const [id, entity] of controller.view.editor.document.entities) {
    if (entity.source.op !== 'text') continue;
    const value = controller.view.editor.scene.values.get(id);
    if (value?.kind !== 'text') continue;
    if (Math.hypot(value.position.x - world.x, value.position.y - world.y) <= LABEL_HIT_RADIUS) {
      return id;
    }
  }
  return null;
}

/** In world units. Generous, because a label is picked by its anchor rather than its box. */
const LABEL_HIT_RADIUS = 22;

function StatusBar({ camera }: { readonly camera: Camera }) {
  /*
   * Labelled fields rather than one run of text.
   *
   * `x 0.0   y 0.0   zoom 100%` is three numbers a reader has to parse apart; three small grey
   * captions with a figure under each is read at a glance. The figures are tabular, so nothing
   * shuffles sideways while the camera moves — which, on a readout that updates every frame of
   * a pan, is the difference between a status bar and a distraction.
   */
  return (
    <div
      className="es-panel"
      style={{
        position: 'absolute',
        left: '50%',
        bottom: space.wide,
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: space.wide,
        padding: '5px 12px',
        borderRadius: radius.pill,
        pointerEvents: 'none',
      }}
    >
      <Readout label="x" value={camera.x.toFixed(1)} />
      <Readout label="y" value={camera.y.toFixed(1)} />
      <Readout label="thu phóng" value={`${(camera.zoom * 100).toFixed(0)}%`} />
    </div>
  );
}

function Readout({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <span style={{ display: 'flex', alignItems: 'baseline', gap: space.snug }}>
      <span className="es-eyebrow">{label}</span>
      <span className="es-numeric" style={{ fontSize: 11, color: colour.body, minWidth: 34 }}>
        {value}
      </span>
    </span>
  );
}

/**
 * Usage hints, collapsed by default.
 *
 * With four panels on screen the canvas is already crowded, and a permanent block of
 * instructions is the first thing an experienced user wants gone. Collapsed it costs one
 * small button; expanded it is there for someone meeting the editor for the first time.
 */
function Hints() {
  const [open, setOpen] = useState(false);

  // Closed by a press on the drawing, like every other thing that floats over it. Reading the
  // hints and then going to try one is the commonest thing to do next, and the panel covering
  // the middle of the canvas while you do it is exactly what it must not do.
  const ref = useRef<HTMLDivElement | null>(null);
  const close = useCallback(() => {
    setOpen(false);
  }, []);
  useDismissOnOutside(ref, close, open);

  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        left: '50%',
        bottom: 46,
        transform: 'translateX(-50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {open && (
        <div
          className="es-panel es-scroll"
          style={{
            maxWidth: 460,
            maxHeight: '52vh',
            overflowY: 'auto',
            padding: '12px 14px',
            font: text.body,
            color: colour.body,
            whiteSpace: 'normal',
            pointerEvents: 'auto',
          }}
        >
          Chọn một công cụ rồi bấm vào những gì nó cần — bảng công cụ đã nhóm sẵn, và di chuột lên
          là thấy trước hình <em>trước khi</em> bạn chốt. Xem trước không hiện gì nghĩa là sẽ không
          dựng được gì: không có tiếp tuyến kẻ từ một điểm nằm trong đường tròn.
          <br />
          <br />
          Phím tắt phân biệt hoa thường: <kbd>m</kbd> là Trung điểm, còn <kbd>M</kbd> là Trung điểm
          của đoạn.
          <br />
          <br />
          Vẽ hai đa giác chồng lên nhau rồi chọn <strong>Ghép hình → Hợp</strong>. Kéo một đỉnh của
          đa giác nào cũng được: hình hợp biến đổi theo, vì nó là một phép dựng chứ không phải một
          ảnh chụp.
          <br />
          <br />
          Bấm đúp một dòng trong <strong>Đối tượng</strong> để đổi tên · hình con mắt để ẩn, ổ khoá
          để khỏi chọn nhầm · mũi tên đổi thứ tự vẽ đè lên nhau.
          <br />
          <br />
          <strong>Xuất ảnh ▾</strong> trên thanh trên cùng lưu bản vẽ thành PNG, SVG, PDF hoặc JPEG.
          SVG và PDF là ảnh vector — phóng to bao nhiêu cũng nét, và bản PDF in được.
          <br />
          <br />
          Thanh ở giữa phía trên là dành cho lúc lên lớp. <strong>Khám phá</strong> cho học sinh kéo
          và đo mà không phá được phép dựng; <strong>Trình chiếu</strong> dọn hết panel đi. Thanh
          trượt phát lại từng bước dựng hình — nó chỉ ẩn bớt trên hình, không bao giờ xoá khỏi tệp.
          <br />
          <br />
          Công cụ <strong>Chữ</strong> (<kbd>t</kbd>) đặt một nhãn; bấm đúp vào nhãn nào để sửa nhãn
          đó. Nhãn viết được ký hiệu toán, gõ như trên bảng và hiện ra đúng dạng:{' '}
          <MathText latex="A_1" />, <MathText latex="x^2" />,{' '}
          <MathText latex={String.raw`\alpha`} />, <MathText latex={String.raw`\angle ABC`} />,{' '}
          <MathText latex={String.raw`90^\circ`} />.
          <br />
          <br />
          Lăn chuột hoặc Space+kéo để di chuyển khung nhìn · Ctrl+lăn để phóng to thu nhỏ ·{' '}
          <kbd>⇧1</kbd> vừa cả bản vẽ, <kbd>⇧2</kbd> vừa phần đang chọn · <kbd>Alt</kbd>+bấm vào một
          cạnh của khối để cho riêng cạnh đó một màu · Esc để huỷ · Del để xoá
        </div>
      )}
      <button
        type="button"
        className="es-btn es-float"
        onClick={() => {
          setOpen((value) => !value);
        }}
        aria-expanded={open}
        style={{
          padding: '3px 12px',
          borderRadius: radius.pill,
          font: text.small,
          color: colour.muted,
          pointerEvents: 'auto',
        }}
      >
        {open ? 'Ẩn gợi ý' : 'Gợi ý'}
      </button>
    </div>
  );
}
